import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { LeadsTableTab } from '@ekhodealer/ekho-common';
import {
	Badge,
	Button,
	ContentWithTooltip,
	Group,
	Stack,
	Tabs,
	Text,
	TextInput,
	ToggleButton,
} from '@ekhodealer/ekho-common/rev';

import type { TabItem } from '@ekhodealer/ekho-common/rev';

import { ExportPopup } from '@common/components/ExportPopup/ExportPopup';
import { Board24, Table24 } from '@common/components/icons';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { paths } from '@config/paths';

import { useExportLeads } from '../api/useExportLeads';
import { useSearchLeads } from '../api/useSearchLeads';
import { activeFiltersCountAtom } from '../atoms/leadsAppliedFiltersAtoms';
import {
	actionRequiredFilterAtom,
	leadsParamsAtom,
	searchValueAtom,
	selectedLeadsAtom,
	updateActionRequiredFilterAtom,
	updateLeadTabFilterAtom,
	updateSearchValueAtom,
	updateStageFilterAtom,
} from '../atoms/leadsAtoms';
import { useActionRequiredCount } from '../hooks/useActionRequiredCount';
import { useLeadsFiltersUrl } from '../hooks/useLeadsFiltersUrl';
import { useLeadsTableTabCounts } from '../hooks/useLeadsTableTabCounts';

import { LeadsFiltersDrawer } from './LeadsFiltersDrawer';
import { LeadStats } from './LeadStats';

interface LeadsToolbarProps {
	viewMode: 'table' | 'board';
	onViewModeChange: (mode: 'table' | 'board') => void;
}

export function LeadsToolbar({ viewMode, onViewModeChange }: LeadsToolbarProps): React.ReactNode {
	const navigate = useNavigate();
	const routeParams = useParams<{ tabId: LeadsTableTab }>();
	const servicesProviderId = useCurrentServicesProviderId();

	// Filter atoms
	const searchValue = useAtomValue(searchValueAtom);
	const updateSearchValue = useSetAtom(updateSearchValueAtom);
	const actionRequiredFilter = useAtomValue(actionRequiredFilterAtom);
	const updateActionRequiredFilter = useSetAtom(updateActionRequiredFilterAtom);

	// Get active filters count from atom
	const activeFiltersCount = useAtomValue(activeFiltersCountAtom);

	// Sync filters with URL for persistence and shareability
	useLeadsFiltersUrl();

	// Export popup data
	const leadsParams = useAtomValue(leadsParamsAtom);
	const { data } = useSearchLeads(leadsParams);
	const selectedLeads = useAtomValue(selectedLeadsAtom);
	const exportMutation = useExportLeads();

	// Get current active tab from URL params
	const activeTab = routeParams.tabId ?? LeadsTableTab.ALL;

	// Sync tab filter atom with URL params (also clears selection)
	const setLeadsTableTabFilter = useSetAtom(updateLeadTabFilterAtom);
	useEffect(() => {
		const tabFromUrl = routeParams.tabId ?? LeadsTableTab.ALL;
		setLeadsTableTabFilter(tabFromUrl);
	}, [routeParams.tabId, setLeadsTableTabFilter]);

	// When All tab is selected, force table view
	useEffect(() => {
		if (activeTab === LeadsTableTab.ALL && viewMode === 'board') {
			onViewModeChange('table');
		}
	}, [activeTab, viewMode, onViewModeChange]);

	// Clear stage filters when switching to board view (doesn't make sense in Kanban)
	const updateStageFilter = useSetAtom(updateStageFilterAtom);
	useEffect(() => {
		if (viewMode === 'board') {
			updateStageFilter([]);
		}
	}, [viewMode, updateStageFilter]);

	// Get tab counts using the hook
	const getTabCount = useLeadsTableTabCounts();

	const actionRequiredCount = useActionRequiredCount();

	// Map tab IDs to their display labels
	const tabLabelMap: Record<LeadsTableTab, string> = {
		[LeadsTableTab.ALL]: 'All',
		[LeadsTableTab.INBOUND]: 'Inbound',
		[LeadsTableTab.DIGITAL_RETAIL]: 'Digital retail',
	};

	// Map tab IDs to their tooltip content
	const tabTooltipMap: Record<LeadsTableTab, string> = {
		[LeadsTableTab.ALL]: 'All leads, across every pipeline.',
		[LeadsTableTab.INBOUND]:
			'Leads from inbound channels such as website forms, widgets, and AI assistants.',
		[LeadsTableTab.DIGITAL_RETAIL]:
			'Leads generated from customers who placed orders directly online.',
	};

	// Build tab items with tooltips and counts
	const tabItems: TabItem[] = Object.values(LeadsTableTab).map((tab) => {
		const count = getTabCount(tab);
		return {
			id: tab,
			content: (
				<ContentWithTooltip
					content={
						<div>
							<Group itemsSpacing='8' itemsAlignY='center' noWrap>
								<Group.Item>
									<span>{tabLabelMap[tab]}</span>
								</Group.Item>
								<Group.Item>
									<Text size='footnote' color='secondary'>
										{count}
									</Text>
								</Group.Item>
							</Group>
						</div>
					}
					tooltipContent={tabTooltipMap[tab]}
					tooltipProps={{ offset: 16 }}
				/>
			),
		};
	});

	const handleTabSelect = (item: TabItem): void => {
		if (!servicesProviderId) {
			return;
		}
		navigate(paths.leadsTab.getHref(servicesProviderId, item.id, viewMode));
	};

	const handleSearchChange = (value: string): void => {
		updateSearchValue(value);
	};

	const handleActionRequiredToggle = (isSelected: boolean): void => {
		updateActionRequiredFilter(isSelected);
	};

	const allFiltersButton = (
		<Button variant='secondary' size='medium'>
			All filters
		</Button>
	);

	return (
		<Stack itemsSpacing='16'>
			<LeadStats />
			<Tabs items={tabItems} selectedKey={activeTab} onSelect={handleTabSelect} />
			<Group itemsAlignY='center' itemsAlignX='space-between' noWrap>
				<Group itemsSpacing='8' noWrap>
					<TextInput
						placeholder='Search'
						type='search'
						size='medium'
						value={searchValue}
						onChange={handleSearchChange}
					/>
					<Group itemsSpacing='8' noWrap>
						<ToggleButton
							variant='outline'
							size='medium'
							isSelected={viewMode === 'table'}
							isDisabled={activeTab === LeadsTableTab.ALL}
							onChange={() => onViewModeChange('table')}
							icon={<Table24 />}
							iconSide='left'>
							Table
						</ToggleButton>
						<ToggleButton
							variant='outline'
							size='medium'
							isSelected={viewMode === 'board'}
							isDisabled={activeTab === LeadsTableTab.ALL}
							onChange={() => onViewModeChange('board')}
							icon={<Board24 />}
							iconSide='left'>
							Board
						</ToggleButton>
					</Group>
				</Group>
				<Group itemsSpacing='8' noWrap>
					<ToggleButton
						variant='outline'
						size='medium'
						isDisabled={actionRequiredCount === 0}
						isSelected={actionRequiredFilter}
						onChange={handleActionRequiredToggle}>
						{`Action required (${actionRequiredCount})`}
					</ToggleButton>
					<ExportPopup
						resourceName='leads'
						currentPageItems={data?.data ?? []}
						totalCount={data?.count ?? 0}
						selectedItems={selectedLeads}
						getItemId={(item) => item.id}
						onExport={(ids) => exportMutation.mutateAsync({ leadIds: ids })}
						isPending={exportMutation.isPending}>
						<Button variant='secondary' size='medium'>
							Export
						</Button>
					</ExportPopup>
					<LeadsFiltersDrawer
						viewMode={viewMode}
						activeTab={activeTab}
						trigger={
							activeFiltersCount > 0 ? (
								<Badge
									label={String(activeFiltersCount)}
									appearance='negative'
									variant='solid'
									size='small'>
									{allFiltersButton}
								</Badge>
							) : (
								allFiltersButton
							)
						}
					/>
				</Group>
			</Group>
		</Stack>
	);
}
